/**
 * Paystack initialization helper — placeholder, key-gated.
 *
 * Reads `REACT_APP_PAYSTACK_PUBLIC_KEY`. When the key is present, opens the
 * Paystack inline checkout (loaded lazily from the Paystack CDN). Otherwise
 * resolves with `{ ok: false, reason: "no_key" }` so callers can fall back
 * to local "demo" success.
 *
 * Usage:
 *   const res = await initPaystackPayment({
 *     email, amount, reference, metadata,
 *     onSuccess(ref) { ... },
 *     onClose() { ... },
 *   });
 */

const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "";
const SDK_URL = "https://js.paystack.co/v2/inline.js";

if (!PAYSTACK_PUBLIC_KEY) {
    // eslint-disable-next-line no-console
    console.warn(
        "[Neyra] Paystack key is not configured yet. " +
        "Set REACT_APP_PAYSTACK_PUBLIC_KEY to accept payments."
    );
}

let sdkPromise = null;
const loadSdk = () => {
    if (typeof window === "undefined") return Promise.reject(new Error("no window"));
    if (window.PaystackPop) return Promise.resolve(window.PaystackPop);
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = SDK_URL;
        s.async = true;
        s.onload = () => resolve(window.PaystackPop);
        s.onerror = () => reject(new Error("Failed to load Paystack SDK"));
        document.head.appendChild(s);
    });
    return sdkPromise;
};

export const isPaystackConfigured = () => Boolean(PAYSTACK_PUBLIC_KEY);

export const initPaystackPayment = async ({
    email,
    amountNgn,
    reference,
    metadata = {},
    onSuccess,
    onClose,
}) => {
    if (!PAYSTACK_PUBLIC_KEY) {
        return { ok: false, reason: "no_key" };
    }
    if (!email) return { ok: false, reason: "no_email" };
    if (!amountNgn || Number(amountNgn) <= 0) return { ok: false, reason: "no_amount" };
    try {
        const PaystackPop = await loadSdk();
        const handler = PaystackPop.setup({
            key: PAYSTACK_PUBLIC_KEY,
            email,
            amount: Math.round(Number(amountNgn) * 100), // kobo
            ref: reference || `NEA-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            metadata,
            currency: "NGN",
            onSuccess: (txn) => onSuccess?.(txn?.reference || reference),
            onClose: () => onClose?.(),
        });
        handler.openIframe();
        return { ok: true };
    } catch (err) {
        return { ok: false, reason: err?.message || "init_failed" };
    }
};
